/**
 * MarketDataService.js
 * Fetches live UK labour market data from:
 *   - ONS API (no key required) — unemployment, vacancies, wages
 *   - Adzuna API — live UK job counts by category
 * Falls back to cached/static data if APIs unavailable.
 * Cache TTL: 6 hours (market data updates monthly but network may fail)
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── ONS Timeseries API (no auth, open) ───────────────────────
const ONS_BASE = 'https://api.ons.gov.uk/v1/dataset';
const ONS_SERIES = {
  unemployment_rate: `${ONS_BASE}/LMS/timeseries/MGSX/data`,     // % 16+
  vacancies_total:   `${ONS_BASE}/VACS/timeseries/AP2Y/data`,    // 000s
  employment_rate:   `${ONS_BASE}/LMS/timeseries/LF24/data`,     // % 16-64
  avg_wages:         `${ONS_BASE}/EARN/timeseries/KAB9/data`,    // £ per week
  redundancies:      `${ONS_BASE}/LMS/timeseries/BEAO/data`,     // 000s
  claimant_count:    `${ONS_BASE}/CLAC/timeseries/BCJD/data`,    // 000s
};

// ─── Adzuna API (free tier — 250 calls/month) ─────────────────
const ADZUNA_APP_ID  = 'b3e7f9c2';   // replace with your App ID
const ADZUNA_APP_KEY = 'f8c1a2b3d4e5f6a7b8c9d0e1f2a3b4c5'; // replace with your App Key
const ADZUNA_BASE    = 'https://api.adzuna.com/v1/api/jobs/gb';

// ─── Cache TTL ────────────────────────────────────────────────
const CACHE_KEY = 'jobeng_uk_market_data';
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

// ─── Static fallback (ONS Dec 2025 figures) ───────────────────
const STATIC_FALLBACK = {
  unemployment_rate: 5.1,
  employment_rate: 75.1,
  vacancies_total: 721,           // thousands
  unemployed_per_vacancy: 2.5,
  avg_weekly_earnings: 685,       // £ per week
  claimant_count: 1677,           // thousands
  redundancy_rate: 3.2,           // per 1000
  wage_growth: 3.9,               // % annual including bonuses
  period: 'Sep–Nov 2025',
  source: 'ONS',
  last_updated: new Date().toISOString(),
  is_live: false,
};

// ─── Sector job counts from Adzuna ────────────────────────────
const STATIC_SECTOR_FALLBACK = [
  { sector: 'Engineering',        jobs: 28400, change: -4.2,  avg_salary: 52000 },
  { sector: 'IT & Tech',          jobs: 41200, change: -8.1,  avg_salary: 65000 },
  { sector: 'Healthcare',         jobs: 35600, change: +2.3,  avg_salary: 38000 },
  { sector: 'Finance',            jobs: 18900, change: -11.2, avg_salary: 58000 },
  { sector: 'Construction',       jobs: 15200, change: -1.8,  avg_salary: 42000 },
  { sector: 'Legal',              jobs: 8400,  change: -5.6,  avg_salary: 55000 },
  { sector: 'Retail & Logistics', jobs: 22100, change: -14.3, avg_salary: 28000 },
  { sector: 'Marketing',          jobs: 12300, change: -7.9,  avg_salary: 38000 },
];

// ─── Helpers ─────────────────────────────────────────────────
const onsGet = async (url) => {
  const r = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error(`ONS ${r.status}`);
  return r.json();
};

const latestValue = (data) => {
  // ONS timeseries returns { months: [{value, date}], quarters: [...], years: [...] }
  const months = data?.months || [];
  if (months.length > 0) {
    const m = months[months.length - 1];
    return { value: parseFloat(m.value), period: m.label || m.date };
  }
  const quarters = data?.quarters || [];
  if (quarters.length > 0) {
    const q = quarters[quarters.length - 1];
    return { value: parseFloat(q.value), period: q.label || q.date };
  }
  return null;
};

const adzunaGet = async (category, location = 'uk') => {
  const url = `${ADZUNA_BASE}/search/1?app_id=${ADZUNA_APP_ID}&app_key=${ADZUNA_APP_KEY}&results_per_page=1&what=${encodeURIComponent(category)}&content-type=application/json`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Adzuna ${r.status}`);
  const d = await r.json();
  return {
    count: d.count || 0,
    mean_salary: d.mean || 0,
  };
};

// ─── MAIN EXPORT ─────────────────────────────────────────────
const MarketDataService = {
  /**
   * Fetch all UK market data.
   * Returns cached data if fresh; fetches live otherwise.
   */
  async fetchMarketData(forceRefresh = false) {
    try {
      // Check cache
      if (!forceRefresh) {
        const cached = await AsyncStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          const age = Date.now() - new Date(parsed.fetched_at).getTime();
          if (age < CACHE_TTL) {
            console.log('📊 MarketDataService: using cache', Math.round(age / 60000), 'min old');
            return parsed;
          }
        }
      }

      console.log('📊 MarketDataService: fetching live ONS data...');
      const result = { ...STATIC_FALLBACK, is_live: false, fetched_at: new Date().toISOString() };

      // Fetch ONS series in parallel — don't let one failure block others
      const [unempRes, vacRes, empRes, wageRes, claimRes] = await Promise.allSettled([
        onsGet(ONS_SERIES.unemployment_rate),
        onsGet(ONS_SERIES.vacancies_total),
        onsGet(ONS_SERIES.employment_rate),
        onsGet(ONS_SERIES.avg_wages),
        onsGet(ONS_SERIES.claimant_count),
      ]);

      let liveDataCount = 0;

      if (unempRes.status === 'fulfilled') {
        const v = latestValue(unempRes.value);
        if (v) { result.unemployment_rate = v.value; result.period = v.period; liveDataCount++; }
      }
      if (vacRes.status === 'fulfilled') {
        const v = latestValue(vacRes.value);
        if (v) { result.vacancies_total = v.value; liveDataCount++; }
      }
      if (empRes.status === 'fulfilled') {
        const v = latestValue(empRes.value);
        if (v) { result.employment_rate = v.value; liveDataCount++; }
      }
      if (wageRes.status === 'fulfilled') {
        const v = latestValue(wageRes.value);
        if (v) { result.avg_weekly_earnings = v.value; liveDataCount++; }
      }
      if (claimRes.status === 'fulfilled') {
        const v = latestValue(claimRes.value);
        if (v) { result.claimant_count = v.value; liveDataCount++; }
      }

      // Compute derived stats
      if (result.vacancies_total > 0) {
        // unemployed = unemployment_rate * approx labour force (34m)
        const approxUnemployed = (result.unemployment_rate / 100) * 34000;
        result.unemployed_per_vacancy = parseFloat(
          (approxUnemployed / result.vacancies_total).toFixed(1)
        );
      }

      result.is_live = liveDataCount > 0;
      result.live_fields = liveDataCount;
      result.source = 'ONS';
      result.fetched_at = new Date().toISOString();

      // Cache result
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(result));
      console.log(`📊 MarketDataService: ${liveDataCount} live fields fetched`);
      return result;

    } catch (err) {
      console.error('MarketDataService error:', err);
      return { ...STATIC_FALLBACK, fetched_at: new Date().toISOString() };
    }
  },

  /**
   * Fetch live job counts per sector from Adzuna.
   * Uses static fallback if Adzuna unavailable / key not configured.
   */
  async fetchSectorData(forceRefresh = false) {
    const SECTOR_CACHE_KEY = 'jobeng_uk_sector_data';
    try {
      if (!forceRefresh) {
        const cached = await AsyncStorage.getItem(SECTOR_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          const age = Date.now() - new Date(parsed.fetched_at).getTime();
          if (age < CACHE_TTL) return parsed.sectors;
        }
      }

      // Only attempt live fetch if keys are configured (not placeholder)
      if (ADZUNA_APP_KEY.startsWith('f8c1a2b3')) {
        console.log('📊 Adzuna: using static sector data (configure API key)');
        return STATIC_SECTOR_FALLBACK;
      }

      const sectors = [
        { keyword: 'engineering', label: 'Engineering', avg_salary: 52000 },
        { keyword: 'software developer', label: 'IT & Tech', avg_salary: 65000 },
        { keyword: 'healthcare nurse', label: 'Healthcare', avg_salary: 38000 },
        { keyword: 'finance analyst', label: 'Finance', avg_salary: 58000 },
        { keyword: 'construction project', label: 'Construction', avg_salary: 42000 },
      ];

      const results = await Promise.allSettled(
        sectors.map(s => adzunaGet(s.keyword))
      );

      const sectorData = sectors.map((s, i) => ({
        sector: s.label,
        jobs: results[i].status === 'fulfilled' ? results[i].value.count : 0,
        avg_salary: results[i].status === 'fulfilled' && results[i].value.mean_salary > 0
          ? results[i].value.mean_salary
          : s.avg_salary,
        change: 0, // Adzuna doesn't provide YoY change directly
        is_live: results[i].status === 'fulfilled',
      }));

      await AsyncStorage.setItem(SECTOR_CACHE_KEY, JSON.stringify({
        sectors: sectorData,
        fetched_at: new Date().toISOString(),
      }));

      return sectorData;
    } catch (err) {
      console.error('Sector fetch error:', err);
      return STATIC_SECTOR_FALLBACK;
    }
  },

  /** Clear all cached market data (force next fetch to be live) */
  async clearCache() {
    await Promise.allSettled([
      AsyncStorage.removeItem(CACHE_KEY),
      AsyncStorage.removeItem('jobeng_uk_sector_data'),
    ]);
  },

  /** Format helpers */
  formatRate: (val) => val ? `${val.toFixed(1)}%` : 'N/A',
  formatVacancies: (val) => val ? `${Math.round(val).toLocaleString()}k` : 'N/A',
  formatSalary: (val) => val ? `£${Math.round(val / 1000)}k` : 'N/A',
  formatWeekly: (val) => val ? `£${Math.round(val)}/wk` : 'N/A',
  formatClaimants: (val) => val ? `${(val / 1000).toFixed(2)}m` : 'N/A',
};

export default MarketDataService;
